import axios from "axios"
import { psApiBaseUri, psHelpCenterUri } from "./constants"
import { ListingParameters, ListingResponseDTO } from "./dto/shared"
import { HelpSearchResponseDTO } from "./dto/help-center"
import { PhotoEditorPlanResponseDTO, PhotoEditorPlansResponseDTO } from "./dto/pixieset-payment"
import { attachAxiosNetworkBehavior } from "./network-behavior"

//Photos uploaded with this runtime flag are from Photo Editor. This value will never change.
const PS_PHOTO_EDITOR_ORIGIN_RUNTIME_FLAG = 9

export const axiosPSClient = axios.create({ baseURL: psApiBaseUri })

export const axiosHelpCenterClient = axios.create({
	baseURL: psHelpCenterUri,
})
attachAxiosNetworkBehavior(axiosPSClient)
attachAxiosNetworkBehavior(axiosHelpCenterClient)

export interface WorkspaceResponseDTO {
	id_hash: string
	subdomain: string
}

export interface CollectionResponseDTO {
	name: string
	event_date: string | null
	expire_at: string | null
	status: number
	collection_download: boolean
	web_download_size: number
	single_download: boolean
	single_download_size: number
	single_download_pin: boolean
	single_download_email: boolean
	cover_template_id: number
	url_key: string
	download_pin: string
	cover_path: string
	cover_width: number
	cover_height: number
	price_id: string
	store: boolean
	high_res_download_size: number
	download_size: string
	create_at: string
	id: string
	tags: string[]
	photo_download: number
	download_client_only: number
	single_download_size_override: number
	video_download: boolean
	slideshow_control: number
	slideshow_enable_looping: number
	slideshow_speed: number
	single_download_limit_enabled: number
	single_download_limit: number
	coverPhoto: string
	coverFocal: string | null
	is_private: boolean
	password: string | null
	private_password: string | null
	photo_count: number
}

export interface BootstrapResponseDTO {
	user: {
		id: string
		username: string
		email: string
		is_password_set: boolean
		icon_url: string
		preferred_locale: string
	}
	profile: {
		business_name: string
		website: string
		phone: string
		address_line_1: string
		country_id: string
		logo_path: string
		full_logo_path: string
		full_logo_ext: string
		cover_logo_path: string
		cover_logo_ext: string
		date_format: string
		google_analytics_id: string
		filename_visibility: number
		search_visibility: number
		favicon_path: string
		favicon_ext: string
		sharpening: number
		timezone: string
		homepage_enabled: number
		homepage_password: string
		homepage_collection_sort: number
		show_website: number
		show_email: number
		show_phone: number
		show_address: number
		show_social_links: number
		show_biography: number
		pixieset_branding: boolean
		use_auto_cover_logo: boolean
		apply_watermark_to_web_sizes: boolean
		contact_email: string
		biography: string
		id: string
		cover_logo_url: string | null
		favicon_url: string | null
		full_logo_url: string | null
		logo_url: string | null
	}
	profileIdp: {
		terms: string | null
		privacy: string | null
		impressum: string | null
		cookie_banner: number
		raw_upload: boolean
	}
	account: {
		status: number
		storage: number
		raw_storage: number
		storage_limit: number
		plan: {
			product: string
			general_name: string
			has_raw_storage: boolean
		}
	}
	customUrl: string | null
	isWorkspaceOwner: boolean
	noticeConfig: {
		showDelinquentNotice: boolean
		showInvalidBillingAddressNotice: boolean
	}
	upgradeDiscount: {
		hasUpgradeDiscount: boolean
		discountCents: number
	}
}

export interface UploadFileDTO {
	name: string
	size: number
	type: string
}

export interface UploadRequestDTO {
	files: UploadFileDTO[]
}

export interface BeforeUploadResponseDTO {
	duplicate_photos: unknown[]
	photo_max_rank: number
	photo_processing_batch_size: string
	has_raw_upload_enabled: boolean
	photos_storage_status: string
	videos_storage_status: string
	raw_photos_storage_status: string
	ups: {
		akid: string
		ep: string
		oep: string
		f: string
		p: string
		s: string
	}
	rts: {
		lm: number
		dl: number
		eps: string[]
	}
}

export interface UploadResponseDTO {
	location: string
}

export interface StorePhotoRequestDTO {
	name: string
	size: number
	type: string
	path: string
	rank: number
}

export interface StorePhotosRequestDTO {
	files: StorePhotoRequestDTO[]
}

export interface StorePhotosResponseDTO {
	data: {
		photos: Array<{
			id: string
			user_id: string
			gallery_id: string
			collection_id: string
			name: string
			mime_type: string
			ext: string
			size: number
			width: number
			height: number
			path: string
			rank: number
			status: number
			capture_date: string
			watermark: number
			mark_private_at: string
			visibility_status: number
			gallery: {
				id: string
				collection_id: string
				name: string
				description: string | null
				photo_count: number
				download: boolean
				private: boolean
			}
		}>
		erroredFiles: unknown[]
		changeDefaultCover: boolean
	}
}

export interface CreateCollectionRequestDTO {
	name: string
	event_date: string | null
	collection_default: boolean | null
	watermark: string | null
	folder: string | null
}

export interface PSListingResponseLinks {
	first: string
	last: string
	prev: string | null
	next: string | null
}

export interface PSListingResponseMetaLink {
	url: string | null
	label: string
	active: boolean
}

export interface PSListingResponseMeta {
	current_page: number
	from: number | null
	last_page: number
	links: PSListingResponseMetaLink[]
	path: string
	per_page: number
	to: number | null
	total: number
}

export interface PSDataResponseDTO<T> {
	data: T
}
export interface PSListingResponseDTO<T> {
	data: T[]
	links: PSListingResponseLinks
	meta: PSListingResponseMeta
}

export interface PhotoResponseDTO {
	id: string
	user_id: string
	gallery_id: number
	collection_id: number
	name: string
	mime_type: string
	ext: string
	size: number
	width: number
	height: number
	path: string
	rank: number
	status: number
	capture_date: string
	watermark: number
	mark_private_at: string
	visibility_status: number
	path_xxlarge: string
	path_xlarge: string
	path_large: string
	path_medium: string
	path_thumb: string
	path_display_small: string
	path_display_medium: string
	path_display_large: string
}

export interface WatermarkResponseDTO {
	id: number
	name: string
	width: number
	height: number
	type: number
	url: string
	opacity: number
	path: string
	position: number
	scale: number
	text: string
	extension: string
	font_color: number
	font_style: number
}

export interface GalleryResponseDTO {
	id: string
	collection_id: number
	name: string
	description: string | null
	photo_count: number
	download: boolean
	private: boolean
	photos?: PhotoResponseDTO[]
}

export interface IntercomDataDTO {
	app_id: string
	user_id: number
	email: string
	username: string
	created_at: string
	user_hash: string
	user_profile_country_id: number
	hide_default_launcher: boolean
	client_gallery_storage: number
	client_gallery_subscription: string
	website_subscription: string
	studio_manager_subscription: string
}

export class PSApiOperations {
	static readonly getWorkspaces = async (): Promise<
		PSListingResponseDTO<WorkspaceResponseDTO>
	> => {
		const response = await axiosPSClient.get<PSListingResponseDTO<WorkspaceResponseDTO>>(
			psApiBaseUri + `photo-editor/workspaces`,
		)
		return response.data
	}

	static readonly getPhotoEditorPlans = async (): Promise<PhotoEditorPlanResponseDTO[]> => {
		const response = await axiosPSClient.get<PhotoEditorPlansResponseDTO>(
			psApiBaseUri + `photo-editor/plans`,
		)

		return response.data.data
	}

	static readonly bootstrap = async (): Promise<BootstrapResponseDTO> => {
		const response = await axiosPSClient.get<PSDataResponseDTO<BootstrapResponseDTO>>(
			psApiBaseUri + `photo-editor/bootstrap`,
		)
		return response.data.data
	}

	static readonly getCollections = async (
		params?: {
			page?: number
			q?: string
		},
		signal?: AbortSignal,
	): Promise<PSListingResponseDTO<CollectionResponseDTO>> => {
		const response = await axiosPSClient.get<
			PSDataResponseDTO<PSListingResponseDTO<CollectionResponseDTO>>
		>(psApiBaseUri + `v1/collections`, {
			params,
			signal,
		})
		return response.data.data
	}

	static readonly getCollection = async (
		collectionId: string,
	): Promise<CollectionResponseDTO> => {
		const response = await axiosPSClient.get<PSDataResponseDTO<CollectionResponseDTO>>(
			psApiBaseUri + `v1/collections/${collectionId}`,
		)
		return response.data.data
	}

	static readonly createCollection = async (
		body: CreateCollectionRequestDTO,
	): Promise<CollectionResponseDTO> => {
		const response = await axiosPSClient.post<PSDataResponseDTO<CollectionResponseDTO>>(
			psApiBaseUri + `v1/collections`,
			body,
		)
		return response.data.data
	}

	static readonly getGalleries = async (
		collectionId: string,
		params?: ListingParameters,
	): Promise<ListingResponseDTO<GalleryResponseDTO>> => {
		const response = await axiosPSClient.get<ListingResponseDTO<GalleryResponseDTO>>(
			psApiBaseUri + `v1/collections/${collectionId}/galleries`,
			{ params },
		)
		return response.data
	}

	static readonly getPhotos = async (params: {
		galleryId: string
	}): Promise<PhotoResponseDTO[]> => {
		const response = await axiosPSClient.get<PSDataResponseDTO<GalleryResponseDTO>>(
			psApiBaseUri + `v1/galleries/${params.galleryId}?expand=photos`,
		)
		return response.data.data.photos ?? []
	}

	static readonly requestUpload = async (params: {
		galleryId: string
		requestBody: UploadRequestDTO
	}): Promise<BeforeUploadResponseDTO> => {
		const response = await axiosPSClient.post<PSDataResponseDTO<BeforeUploadResponseDTO>>(
			psApiBaseUri + `v1/galleries/${params.galleryId}/before_upload`,
			params.requestBody,
		)
		return response.data.data
	}

	/**
	 * Sends a POST request to store multiple photos in client gallery using the Pixieset API.
	 *
	 * @param response - The Axios response containing the result of the bulk photo store operation.
	 * @returns The response data of type `StorePhotosResponseDTO` from the API.
	 *
	 * @remarks
	 * The request includes additional runtime information via the `PS_PHOTO_EDITOR_ORIGIN_RUNTIME_FLAG`. This runtime information will not change and should not be modifiable by callers.
	 * The endpoint used is `/v1/galleries/{galleryId}/photos/bulk_store`.
	 */
	static readonly storePhotos = async (params: {
		galleryId: string
		requestBody: StorePhotosRequestDTO
	}): Promise<StorePhotosResponseDTO> => {
		const response = await axiosPSClient.post<StorePhotosResponseDTO>(
			psApiBaseUri + `v1/galleries/${params.galleryId}/photos/bulk_store`,
			{ ...params.requestBody, runtime: PS_PHOTO_EDITOR_ORIGIN_RUNTIME_FLAG },
		)

		return response.data
	}

	/**
	 * Deletes multiple photos from a specified gallery.
	 * @warning This method is only meant for cleaning up photos that users intentionally hide after publishing to the Client Gallery.
	 * It should not be used to manage or delete photos in the collection for any other reason.
	 * @param params.galleryId - The unique identifier of the gallery containing the photos.
	 * @param params.photoIds - An array of photo IDs to be deleted.
	 */
	static readonly deletePhotos = async (params: {
		galleryId: string
		photoIds: string[]
	}): Promise<void> => {
		await axiosPSClient.post(
			psApiBaseUri + `v1/galleries/${params.galleryId}/photos/bulk_destroy`,
			{ ids: params.photoIds },
		)
	}

	static readonly getWatermarks = async () => {
		const result = await axiosPSClient.get<PSDataResponseDTO<WatermarkResponseDTO[]>>(
			psApiBaseUri + `v1/watermarks`,
		)
		return result.data.data
	}

	static readonly setWatermark = async (params: {
		collectionId: number
		watermarkId: number | null
	}): Promise<void> => {
		await axiosPSClient.patch(
			psApiBaseUri + `v1/collections/${params.collectionId}/update_default_watermark`,
			{
				watermark_id: params.watermarkId,
			},
		)
	}

	static readonly searchHelpArticles = async (
		params: {
			route: string
			application: string
			search: string
			results: number
		},
		signal?: AbortSignal,
	): Promise<HelpSearchResponseDTO> => {
		const response = await axiosHelpCenterClient.get<HelpSearchResponseDTO>(
			psHelpCenterUri + "/v1/kb/search",
			{
				params,
				signal,
			},
		)
		return response.data
	}

	static readonly getIntercomData = async (): Promise<IntercomDataDTO> => {
		const response = await axiosPSClient.get<PSDataResponseDTO<IntercomDataDTO>>(
			psApiBaseUri + `v1/data/intercom`,
		)
		return response.data.data
	}
}
